/**
 * AVS-4422: which car one `<avp-3dws-deck>` mount was asked to show, so a start-up echo can be
 * named in the log.
 *
 * The deck can drop a `pr-string` the host writes during its start-up and then announce the
 * AVE's older car (`update:pr-string`), the "start-up echo". The tracker keeps `held`, every car
 * the deck may hold, and a pending selection: the `target` the host wrote and the cars it
 * `replaced`. An announcement of a replaced car is an `echo`; any other is `apply`. Every
 * announcement settles: the deck holds the announced car and pending ends.
 *
 * Only start-up drops a write. Past its first car sync after `streamInitialized` the deck selects a
 * write silently, so that sync closes pending and later writes open none. A failure or a new
 * `streamInitialized` reopens the window.
 *
 * AVS-4483: past start-up the deck can still report the engine's old car while a written car loads,
 * then write it onto its own `pr-string` and re-select it. So every later write is `inFlight` for the
 * deck's select timeout, and a report of a car it replaced is `stale`: the host's pick stands. A report
 * of a third car, a new write, a failure or `streamInitialized` ends it early; a report of the written
 * car does not, since an older report can still arrive after it.
 *
 * The caller forwards every announcement with its verdict and never writes back to the deck
 * (re-writing the target raced the deck's own re-select of the old car). Protection lives in the
 * vehicle registry, which registers a car no entry holds only for an `echo`. Pure per-mount state:
 * no DOM, no timers, no logging. A customer tab passes `guard: false` and every announcement applies.
 */
export type DeckAnnouncement = 'apply' | 'echo' | 'stale';

/** The deck's own AVE request timeout (deck v1.104.1: `config.aveMessageTimeout ?? 30` seconds). */
export const DECK_SELECT_TIMEOUT_MS = 30_000;

export interface IDeckSelectionPending {
  readonly replaced: ReadonlySet<string>;
  readonly target: string;
}

export interface IDeckSelection {
  /** The host rendered a new `pr-string` onto the deck. */
  written(next: string): void;
  /** The deck announced `value` via `update:pr-string`; `echo` names it for the log and the registry. */
  announced(value: string): DeckAnnouncement;
  /** The deck fired `exception` or `disconnected`. */
  failed(): void;
  /** The deck fired `streamInitialized`. */
  initialized(): void;
  /**
   * The deck's car sync ended, after any announcement it carried. Only the first after `initialized`
   * counts. Returns the pending selection it closed, which the deck kept.
   */
  synced(): IDeckSelectionPending | undefined;
  readonly pending: IDeckSelectionPending | undefined;
  /** A write past start-up the deck may still be loading (AVS-4483); reports of `replaced` are stale. */
  readonly inFlight: IDeckSelectionPending | undefined;
  readonly held: ReadonlySet<string>;
  /**
   * The cars the selection the first car sync closed had replaced, until the next announcement, write,
   * `streamInitialized` or failure. An announcement of one of them is another client's pick or a late
   * start-up echo, which would mean the deck changed its event order.
   */
  readonly acceptedAtSync: ReadonlySet<string> | undefined;
}

interface IPendingState {
  replaced: Set<string>;
  target: string;
}

interface IInFlightState extends IPendingState {
  at: number;
}

export function createDeckSelection(
  mountedWith: string,
  { guard, now = () => performance.now() }: { guard: boolean; now?: () => number }
): IDeckSelection {
  let held = new Set([mountedWith]);
  let pending: IPendingState | undefined;
  let inFlight: IInFlightState | undefined;
  let phase: 'starting' | 'initialized' | 'started' = 'starting';
  let acceptedAtSync: Set<string> | undefined;

  const snapshot = (state: IPendingState): IDeckSelectionPending =>
    Object.freeze({ replaced: new Set(state.replaced), target: state.target });

  // The deck gives up on a select after its timeout, so a window that old carries nothing forward.
  const dropTimedOut = (): void => {
    if (inFlight && now() - inFlight.at >= DECK_SELECT_TIMEOUT_MS) inFlight = undefined;
  };

  return {
    written(next) {
      acceptedAtSync = undefined;
      if (!guard) return;

      if (phase === 'started') {
        dropTimedOut();
        if (next === inFlight?.target) return;
        const replaced = new Set([...held, ...(inFlight?.replaced ?? [])]);
        replaced.delete(next);
        inFlight = replaced.size ? { replaced, target: next, at: now() } : undefined;
        held = new Set([next]);
        return;
      }

      if (pending) {
        if (next === pending.target) return;
        pending.replaced.add(pending.target);
        pending.replaced.delete(next);
        pending.target = next;
        return;
      }

      if (held.size === 1 && held.has(next)) return;
      const replaced = new Set(held);
      replaced.delete(next);
      pending = { replaced, target: next };
    },

    announced(value) {
      // Pending and in-flight only ever open with the guard on, so a customer tab always applies.
      dropTimedOut();
      if (inFlight?.replaced.has(value)) return 'stale';
      // A report of the written car keeps the window: an older report may still follow it.
      if (value !== inFlight?.target) inFlight = undefined;
      const echo = pending?.replaced.has(value) === true;
      held = new Set([value]);
      pending = undefined;
      acceptedAtSync = undefined;
      return echo ? 'echo' : 'apply';
    },

    failed() {
      phase = 'starting';
      acceptedAtSync = undefined;
      dropTimedOut();
      // Pending (start-up) and in-flight (past it) never overlap, and each already covers `held`.
      const open = pending ?? inFlight;
      pending = undefined;
      inFlight = undefined;
      if (open) held = new Set([...open.replaced, open.target]);
    },

    initialized() {
      phase = 'initialized';
      acceptedAtSync = undefined;
      inFlight = undefined;
    },

    synced() {
      if (phase !== 'initialized') return undefined;
      phase = 'started';
      if (!pending) return undefined;
      const closed = snapshot(pending);
      held = new Set([pending.target]);
      acceptedAtSync = new Set(pending.replaced);
      pending = undefined;
      return closed;
    },

    get pending() {
      return pending ? snapshot(pending) : undefined;
    },

    get inFlight() {
      return inFlight ? snapshot(inFlight) : undefined;
    },

    get held() {
      return new Set(held);
    },

    get acceptedAtSync() {
      return acceptedAtSync ? new Set(acceptedAtSync) : undefined;
    },
  };
}
